// src/admin/legal/LegalDocumentsPage.jsx — «Configurazione» → «TOS & Privacy» (v219), route
// /admin/settings/legal-documents (superadmin). The versions of the Terms of Service and the Privacy Policy:
// a summary of what is in force, the table, Brian's PDF copy, «Nuova versione» and «Correggi».
// Contract: <BACKEND_FOLDER>/docs/frontend/2026-10-30-legal-document-versions-screen-handoff.md (BE ≥ 6991a98).
//
// ⛔ A sequence ticket guards every read (then / catch / finally): a stale answer never lands over a fresh one.
// ⛔ After a write the list is RE-READ FIRST and only then the OutcomeDialog opens (CLAUDE.md "Action outcomes").
// ⛔ Never err.message: refusals are worded by legalErrorCopy.js. A 404 HTTP_ERROR (old backend) = «non disponibile».
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus } from 'lucide-react';
import OutcomeDialog from '../../components/ui/OutcomeDialog.jsx';
import { triggerBlobDownload } from '../../utils/blobDownload.js';
import { listLegalDocuments, downloadLegalPdf } from '../legalDocumentsApi.js';
import { normaliseList, documentLabelKey, formatDateTime } from './legalModel.js';
import { legalErrorCopy } from './legalErrorCopy.js';
import LegalSummary from './LegalSummary.jsx';
import LegalVersionsTable from './LegalVersionsTable.jsx';
import AddVersionModal from './AddVersionModal.jsx';
import CorrectVersionModal from './CorrectVersionModal.jsx';
import { alertStyle, buttonStyle } from './legalFormStyles.js';
import { COLORS, FONT_SANS } from '../../constants/theme.js';

export default function LegalDocumentsPage() {
  const { t, i18n } = useTranslation('admin');
  const [list, setList] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [outcome, setOutcome] = useState(null);
  const [adding, setAdding] = useState(false);
  const [correcting, setCorrecting] = useState(null);
  const ticket = useRef(0);

  const load = useCallback(async () => {
    ticket.current += 1;
    const mine = ticket.current;
    setLoading(true);
    setError('');
    try {
      const raw = await listLegalDocuments();
      if (ticket.current !== mine) return;
      setList(normaliseList(raw, Date.now()));
    } catch (err) {
      if (ticket.current !== mine) return;
      setList(null);
      const { key, params } = legalErrorCopy(err);
      setError(t(key, params));
    } finally {
      if (ticket.current === mine) setLoading(false);
    }
  }, [t]);

  useEffect(() => { load(); }, [load]);

  async function handleDownload(row) {
    setNotice('');
    try {
      const blob = await downloadLegalPdf(row.document, row.version);
      triggerBlobDownload(blob, `${row.document}-${row.version}.pdf`);
    } catch (err) {
      const copy = legalErrorCopy(err);
      if (copy.reload) await load();
      setNotice(t(copy.key, copy.params));
    }
  }

  const docName = (document) => (documentLabelKey(document) ? t(documentLabelKey(document)) : document);

  // ⛔ Re-read FIRST, then close and report (CLAUDE.md): the table behind «OK» is already the new one.
  async function handleAdded(row, { scheduled }) {
    await load();
    setAdding(false);
    setOutcome({
      tone: 'success',
      text: scheduled
        ? t('legalDocuments.outcome.addedScheduled', {
          version: row.version, document: docName(row.document), date: formatDateTime(row.effective_from, i18n.language),
        })
        : t('legalDocuments.outcome.addedNow', { version: row.version, document: docName(row.document) }),
    });
  }

  async function handleCorrected(row) {
    await load();
    setCorrecting(null);
    setOutcome({
      tone: 'success',
      text: t('legalDocuments.outcome.corrected', { version: row.version, document: docName(row.document) }),
    });
  }

  return (
    <div style={{ padding: '24px 40px 40px', fontFamily: FONT_SANS, color: COLORS.ink, maxWidth: '1200px' }}>
      <p style={{ color: COLORS.textSecondary, fontSize: '13px', margin: '0 0 20px', maxWidth: '760px' }}>
        {t('legalDocuments.subtitle')}
      </p>

      {error && <div role="alert" style={alertStyle}>{error}</div>}
      {notice && <div role="alert" style={alertStyle}>{notice}</div>}

      {list && <LegalSummary list={list} />}

      {(list || loading) && (
        <>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '12px' }}>
            <button
              type="button" style={buttonStyle(true, !list)} disabled={!list}
              onClick={() => setAdding(true)}
            >
              <Plus size={14} style={{ verticalAlign: '-2px', marginRight: '6px' }} />
              {t('legalDocuments.add')}
            </button>
          </div>
          <LegalVersionsTable list={list} loading={loading && !list} onDownload={handleDownload} onCorrect={setCorrecting} />
        </>
      )}

      {adding && <AddVersionModal onClose={() => setAdding(false)} onAdded={handleAdded} />}
      {correcting && (
        <CorrectVersionModal
          row={correcting} onClose={() => setCorrecting(null)} onCorrected={handleCorrected} onStale={load}
        />
      )}
      <OutcomeDialog outcome={outcome} onClose={() => setOutcome(null)} />
    </div>
  );
}
